import { Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

interface NavbarProps {
  language: string;
}

const NavbarDinner = ({ language }: NavbarProps) => {
  const location = useLocation();
  const [showScrollHint, setShowScrollHint] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const links = [
    { path: "/EntreesDinner", fr: "ENTRÉES", en: "STARTERS" },
    { path: "/MainDinner", fr: "PRINCIPAUX", en: "MAIN" },
    { path: "/DessertDinner", fr: "DESSERTS", en: "DESSERTS" },
    { path: "/DrinksDinner", fr: "BOISSONS", en: "DRINKS" },
  ];

  // Scroll vers l'élément actif
  useEffect(() => {
    if (containerRef.current && isMobile) {
      const activeLink = containerRef.current.querySelector('.active-nav-item');
      if (activeLink) {
        activeLink.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      if (containerRef.current && mobile) {
        const isOverflowing = containerRef.current.scrollWidth > containerRef.current.clientWidth;
        setShowScrollHint(isOverflowing && !hasInteracted);
      } else {
        setShowScrollHint(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    const handleInteraction = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
        setShowScrollHint(false);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleInteraction);
      container.addEventListener('touchstart', handleInteraction);
    }

    const timer = setTimeout(() => {
      checkIfMobile();
    }, 1000);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (container) {
        container.removeEventListener('scroll', handleInteraction);
        container.removeEventListener('touchstart', handleInteraction);
      }
      clearTimeout(timer);
    };
  }, [hasInteracted]);

  return (
    <div className="fixed w-full top-[50px] z-40 bg-transparent">
      <div className="relative">
        {/* Effet visuel de scroll */}
        {isMobile && showScrollHint && (
          <>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 left-0 h-full w-32 bg-gradient-to-r from-green-400/20 to-transparent animate-swipe-hint"></div>
            </div>

            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 mt-12">
              <div className="flex flex-col items-center">
                <span className="text-xs font-bold text-green-950 mb-1">
                  {language === "fr" ? "GLISSER" : "SWIPE"}
                </span>
                <div className="relative w-10 h-10">
                  <div className="absolute inset-0 bg-green-600/20 rounded-full animate-ping"></div>
                  <div className="relative flex items-center justify-center w-10 h-10">
                    <svg
                      className="w-6 h-6 text-green-950 animate-swipe-horizontal"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div
          ref={containerRef}
          className="nav-container w-full overflow-x-auto scroll-smooth no-scrollbar"
        >
          <div className="flex justify-center md:justify-center space-x-4 py-2 backdrop-blur-md bg-white/10 shadow-sm ring-1 ring-white/20 h-full min-w-max px-4">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-5 py-2 text-sm font-medium transition-all duration-300 flex items-center justify-center whitespace-nowrap ${
                  location.pathname === link.path
                    ? "bg-green-950 text-white h-full active-nav-item"
                    : "text-green-950 hover:bg-transparent h-full"
                }`}
              >
                {language === "fr" ? link.fr : link.en}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Styles d'animation */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes swipe-hint {
          0% { transform: translateX(0); opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateX(100px); opacity: 0; }
        }
        @keyframes swipe-horizontal {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(5px); }
        }
        .animate-swipe-hint {
          animation: swipe-hint 2.5s infinite;
        }
        .animate-swipe-horizontal {
          animation: swipe-horizontal 1.5s infinite;
        }
      `}</style>
    </div>
  );
};

export default NavbarDinner;